import { useCallback, useState } from 'react';
import { usePortalDropdown } from '../hooks/usePortalDropdown';
import type { MentionCandidate } from '../../state/mentionUtils';
import styles from './DialogueLines.module.css';

/**
 * セリフ行の話者ピッカー (DialogueLinesEditor) 用ドロップダウン状態。
 * 映像・ビジュアル欄の @メンション候補ポップアップ (MaterialMentionPopup) を見た目・
 * 候補行構造そのままで流用しつつ、こちらは「テキスト入力中の @」ではなく
 * 「トリガー要素のクリック/キー操作」で開閉する (docs/features/scenario-dialogue-speaker-plan.md §3.8)。
 *
 * 位置決め・外側クリックで閉じる・スクロール追従は usePortalDropdown
 * (ScenarioGenerator のモデル選択ドロップダウン等と共通) にそのまま乗せる。
 * 1エディタにつきこのフックを1つだけ生成し、行番号を id として渡すことで
 * 「同時に開けるのは1行だけ」という自然な挙動になる。
 */
export function useDialogueSpeakerDropdown() {
  const dropdown = usePortalDropdown({ triggerClassName: styles.speakerTrigger });
  const [activeIndex, setActiveIndex] = useState(0);

  const isOpenFor = useCallback((rowId: string) => dropdown.openId === rowId, [dropdown.openId]);

  /** トリガークリック/Enter/Space での開閉。開くときは現在値の位置をハイライト初期値にする。 */
  const toggle = useCallback((
    rowId: string,
    triggerEl: HTMLElement,
    candidates: MentionCandidate[],
    currentValue: string,
  ) => {
    if (dropdown.openId !== rowId) {
      const idx = candidates.findIndex((c) => c.target.id === currentValue);
      setActiveIndex(idx === -1 ? 0 : idx);
    }
    dropdown.open(rowId, triggerEl);
  }, [dropdown]);

  /**
   * 任意のハイライト初期位置を指定して開く (行追加直後の自動オープン専用。
   * 「直前行の話者と異なる最初の候補」を呼び出し側で計算して渡す。
   * `toggle` と違い、現在値からの逆引きは行わない — 新規行は常に未選択なため。
   */
  const openWithActiveIndex = useCallback((
    rowId: string,
    triggerEl: HTMLElement,
    initialActiveIndex: number,
  ) => {
    setActiveIndex(initialActiveIndex);
    dropdown.open(rowId, triggerEl);
  }, [dropdown]);

  /** 開いている行のポップアップ内でのキー操作 (↑↓/Enter/Esc)。閉じている行では何もしない。 */
  const handleTriggerKeyDown = useCallback((
    event: React.KeyboardEvent,
    rowId: string,
    triggerEl: HTMLElement,
    candidates: MentionCandidate[],
    currentValue: string,
    onSelect: (candidate: MentionCandidate) => void,
  ) => {
    if (dropdown.openId !== rowId) {
      // 閉じている行では Enter / Space / ArrowDown で開く (ネイティブ select の慣習に合わせる)。
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        toggle(rowId, triggerEl, candidates, currentValue);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      dropdown.close();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((idx) => (candidates.length === 0 ? 0 : (idx + 1) % candidates.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((idx) => (candidates.length === 0 ? 0 : (idx - 1 + candidates.length) % candidates.length));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = candidates[activeIndex];
      if (chosen) {
        onSelect(chosen);
        dropdown.close();
      }
    }
  }, [dropdown, activeIndex, toggle]);

  return {
    dropdown,
    activeIndex,
    setActiveIndex,
    isOpenFor,
    toggle,
    openWithActiveIndex,
    handleTriggerKeyDown,
  };
}
